import Button from 'components/Button';
import ClientContact, {
    ClientContactData,
} from 'components/Contact/ClientContact';
import FormResult from 'components/Form/FormResult';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TooltipWrapper from 'components/Tooltip';
import {H1} from 'components/Typography';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {Backend} from 'types/api';
import {PermissionResponse} from 'types/permissions';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';

import {
    usePostArchive,
    usePostDeleteOrganization,
    usePostRemoveContacts,
    usePostSetDefaultContacts,
} from './services';

export type ClientContactsBackend = Backend<
    {
        client: {
            mid: string;
            name: string;
            permissions: {
                canArchive: PermissionResponse;
                canDelete: PermissionResponse;
            };
        };
        clientContacts: Array<ClientContactData>;
    },
    'canAddCompanyContact' | 'canAddPersonalContact'
>;

type ClientContactsFormData = FormFields<{
    action: string;
}>;

const compareClientContacts = (a: ClientContactData, b: ClientContactData) => {
    let output = 0;

    if (a.isCompany) {
        output = -1;
    } else if (b.isCompany) {
        output = 1;
    } else if (a.isDefault) {
        output = -1;
    } else if (b.isDefault) {
        output = 1;
    }

    return output;
};

const Contact = () => {
    const {appPermissions, data, refreshData} =
        useBackend<ClientContactsBackend>();

    const methods = useForm<ClientContactsFormData>();
    const {
        formState: {isSubmitting},
        handleSubmit,
        reset,
        setValue,
        watch,
    } = methods;

    const action = watch('action');

    const {mutateAsync: mutateAsyncPostArchive} = usePostArchive();

    const {mutateAsync: mutateAsyncPostDeleteOrganization} =
        usePostDeleteOrganization();

    const {mutateAsync: mutateAsyncSetDefault} = usePostSetDefaultContacts();

    const {mutateAsync: mutateAsyncRemoveContact} = usePostRemoveContacts(
        data.client.mid,
    );

    const onArchive = () => {
        setValue('action', 'archive');
        handleSubmit(async () => {
            await mutateAsyncPostArchive({
                formData: {},
                mid: data.client.mid,
            })
                .then(() => {
                    setUrl(window.Urls.client_list_view());
                })
                .catch(onApiFormError(methods))
                .finally(reset);
        })();
    };

    const onDeleteOrganization = () => {
        setValue('action', 'delete');
        handleSubmit(async () => {
            await mutateAsyncPostDeleteOrganization({
                formData: {},
                mid: data.client.mid,
            })
                .then(() => {
                    setUrl(window.Urls.client_list_view());
                })
                .catch(onApiFormError(methods))
                .finally(reset);
        })();
    };

    const onSetDefaultContact = (contactId: string) => {
        setValue('action', `setDefault${contactId}`);
        handleSubmit(async () => {
            await mutateAsyncSetDefault({
                clientMid: data.client.mid,
                contactId,
                formData: {},
            })
                .then(() => {
                    refreshData(`/home/client/${data.client.mid}/contact/`);
                })
                .catch(onApiFormError(methods))
                .finally(reset);
        })();
    };

    const onRemoveContact = (contactId: string) => {
        setValue('action', `remove${contactId}`);
        handleSubmit(async () => {
            await mutateAsyncRemoveContact({
                contactId,
                formData: {},
            })
                .then(() => {
                    refreshData(`/home/client/${data.client.mid}/contact/`);
                })
                .catch(onApiFormError(methods))
                .finally(reset);
        })();
    };

    return (
        <FormProvider {...methods}>
            <header className="flex flex-col">
                <div className="flex items-start justify-between">
                    <H1 className="m-0">{data.client.name}</H1>
                    <div className="flex items-start gap-2">
                        <TooltipWrapper
                            message={
                                data.client.permissions.canArchive.failureReason
                            }
                        >
                            <Button
                                disabled={
                                    !data.client.permissions.canArchive.allowed
                                }
                                icon="archive"
                                isLoading={isSubmitting && action === 'archive'}
                                kind="secondary"
                                onClick={onArchive}
                            >
                                {t`Archive Client`}
                            </Button>
                        </TooltipWrapper>
                        <TooltipWrapper
                            message={
                                data.client.permissions.canDelete.failureReason
                            }
                        >
                            <Button
                                disabled={
                                    !data.client.permissions.canDelete.allowed
                                }
                                icon="trash"
                                isLoading={isSubmitting && action === 'delete'}
                                kind="destructive"
                                onClick={onDeleteOrganization}
                            >
                                {t`Delete`}
                            </Button>
                        </TooltipWrapper>
                    </div>
                </div>
                <NavigationTabs.Client
                    mid={data.client.mid}
                    selection="contact"
                />
                <FormResult />
            </header>
            <main className="flex flex-col gap-4 py-4">
                {!data.clientContacts.some(
                    (clientContact) => clientContact.isCompany,
                ) && (
                    <TooltipWrapper
                        message={
                            appPermissions.canAddCompanyContact.failureReason
                        }
                    >
                        <LinkButton
                            aria-label={t`Add Company Information`}
                            className="w-fit"
                            disabled={
                                !appPermissions.canAddCompanyContact.allowed
                            }
                            href={window.Urls.client_contact_organization_create_view(
                                data.client.mid,
                            )}
                            icon="add"
                            kind="primary"
                        >
                            {t`Add Company Information`}
                        </LinkButton>
                    </TooltipWrapper>
                )}
                {data.clientContacts
                    .sort(compareClientContacts)
                    .map((clientContact) => (
                        <ClientContact
                            key={clientContact.mid}
                            {...clientContact}
                            idClient={data.client.mid}
                            isLoadingRemove={
                                isSubmitting &&
                                action === `remove${clientContact.mid}`
                            }
                            isLoadingSetDefault={
                                isSubmitting &&
                                action === `setDefault${clientContact.mid}`
                            }
                            onRemoveContact={() =>
                                onRemoveContact(clientContact.mid)
                            }
                            onSetDefaultContact={() =>
                                onSetDefaultContact(clientContact.mid)
                            }
                            permissions={{
                                canDelete: clientContact.permissions.canDelete,
                                canEdit: clientContact.permissions.canEdit,
                                canSetAsDefault:
                                    clientContact.permissions.canSetAsDefault,
                            }}
                        />
                    ))}
                {data.clientContacts.some(
                    (clientContact) => clientContact.isCompany,
                ) && (
                    <TooltipWrapper
                        message={
                            appPermissions.canAddPersonalContact.failureReason
                        }
                    >
                        <LinkButton
                            aria-label={t`Add a Personal Contact`}
                            className="w-fit"
                            disabled={
                                !appPermissions.canAddPersonalContact.allowed
                            }
                            href={window.Urls.client_contact_person_create_view(
                                data.client.mid,
                            )}
                            icon="add"
                            kind="primary"
                        >
                            {t`Add a Personal Contact`}
                        </LinkButton>
                    </TooltipWrapper>
                )}
            </main>
        </FormProvider>
    );
};

export default Contact;
